import { useEffect, useState } from 'react';
import { toast } from '@acast-tech/decibel';
import { useDerivedNetworkContext, useFilterContext } from '@/context';
import useFilterNavigation from '@/hooks/useFilterNavigation';
import useAllTimePreset from '@/hooks/useAllTimePreset';
import { validateDateRange } from '@/utils';
import { DateRange } from '@/types';

const DATE_RANGE_TOAST_SUFFIX = ' You have been redirected to the default date range.';

interface UseSyncUrlToFiltersOptions {
  /** When true, page requires a single show: redirect when showIds is empty or not in network,
   * using the first show rather than an empty selection as the fallback. */
  requireSingleShow?: boolean;
  /** When true, `selectedShows` is owned by {@link useSelectedShows} — only date range is synced
   * here. */
  multiShowMode?: boolean;
}

export interface UseSyncUrlToFiltersResult {
  /** `true` after the first successful sync; use as an `enabled` guard on data queries. */
  isSynced: boolean;
}

/**
 * Syncs URL params (showIds, from, to) to filter state. Reads the current URL shape via
 * `useFilterNavigation` (path params or query string, depending on the active strategy).
 * Redirects when a show is not in the network or when the date range is invalid.
 * Used by Earnings, Audience, and Content Performance pages.
 *
 * Returns `{ isSynced }` to gate data queries until the first sync completes.
 */
export function useSyncUrlToFilters({
  requireSingleShow = false,
  multiShowMode = false,
}: UseSyncUrlToFiltersOptions = {}): UseSyncUrlToFiltersResult {
  const { setFilters } = useFilterContext();
  const { selectedNetworks, shows } = useDerivedNetworkContext();
  const { currentParams, getDefaultDateRange, goToValidParams } = useFilterNavigation();
  const { showIds, from, to } = currentParams;
  // TODO: remove once `allTimePreset` is enabled for everyone (also drop it from the effect deps).
  const isAlltimePresetEnabled = useAllTimePreset();

  // Key the sync on the full selected-network list (in path mode this is a single network) rather
  // than just the primary id, so a multi-network change like `?networks=A,B` -> `A,C` re-syncs even
  // when the primary network is unchanged.
  // Include the full show selection so an in-network show change (e.g. browser back/forward, or
  // adding/removing a non-first show in multiShowMode) drops isSynced for one render cycle until
  // the effect fires and updates filters.selectedShows in the same batch.
  const selectedNetworkKey = `${selectedNetworks.map((network) => network.id ?? '').join(',')}::${showIds.join(',') || 'all'}`;

  const [syncedNetworkKey, setSyncedNetworkKey] = useState<string | null>(null);
  const isSynced = syncedNetworkKey === selectedNetworkKey;

  useEffect(() => {
    // Single-show pages need a real show to redirect to; wait until shows are available.
    // Earnings allows "all", so empty shows still proceed (redirect unknown ids to "all" and sync dates).
    if (requireSingleShow && shows.length === 0) {
      return;
    }

    const validShowIds = showIds.filter((id) => shows.some((show) => show.id === id));
    const showNotInNetwork = validShowIds.length !== showIds.length;
    const needsShowRedirect = showNotInNetwork || (requireSingleShow && showIds.length === 0);

    // Preserve every id that is still valid instead of collapsing the whole selection to "all" or
    // a single show — only fall back once no selected id remains valid.
    const resolvedShowIds =
      validShowIds.length > 0
        ? validShowIds
        : requireSingleShow && shows[0]
          ? [shows[0].id]
          : [];

    const dateRangeValidations = validateDateRange(from, to, isAlltimePresetEnabled);

    if (!needsShowRedirect && dateRangeValidations?.isValid) {
      setFilters((prev) => ({
        ...prev,
        ...(multiShowMode ? {} : { selectedShows: resolvedShowIds }),
        dateRange: { from: from!, to: to! } as DateRange,
      }));
      setSyncedNetworkKey(selectedNetworkKey);
      return;
    }

    const defaultDateRange = getDefaultDateRange();
    goToValidParams(
      resolvedShowIds,
      dateRangeValidations?.isValid ? (from ?? defaultDateRange.from) : defaultDateRange.from,
      dateRangeValidations?.isValid ? (to ?? defaultDateRange.to) : defaultDateRange.to
    );

    if (dateRangeValidations?.error) {
      toast.danger(`${dateRangeValidations.error}.${DATE_RANGE_TOAST_SUFFIX}`);
      console.error(dateRangeValidations.error);
    }
  }, [
    showIds,
    from,
    to,
    shows,
    selectedNetworkKey,
    setFilters,
    getDefaultDateRange,
    goToValidParams,
    requireSingleShow,
    multiShowMode,
    isAlltimePresetEnabled,
  ]);

  return { isSynced };
}
